/**
 * Manual tour upload — the dashboard's "Upload tour" page.
 *
 * Registers a tour that was built outside the legacy pipeline: today, an
 * iOS capture processed by mesh-lab on a Mac into the folder shape the web
 * viewer reads (`tourDetails.json`, `floors/…/model.drc`, panorama tile
 * pyramids). Uploading is a copy, not a conversion.
 *
 * Path, mirroring tourVideos.ts:
 *   1. `tourUploadBegin` mints a tour id and a storage prefix.
 *   2. Every file streams straight to Firebase Storage under that prefix
 *      with `uploadBytesResumable` — any size, any count; the 32 MB Cloud
 *      Functions body limit never applies.
 *   3. `tourUploadFinalize` validates what landed, rewrites the manifest's
 *      storage keys to the minted id, makes the objects public and writes
 *      the Firestore documents that put the tour in the dashboard list.
 *
 * All three calls carry the signed-in user's ID token; the function resolves
 * it to the legacy creator id itself, so nothing about identity comes from
 * the browser.
 */
import { getAuth } from 'firebase/auth';
import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
} from 'firebase/storage';

function baseUrl(): string {
  // Same Cloud Functions domain as the other tour-media APIs; a dedicated
  // variable lets a dev deploy point only this feature elsewhere.
  const url =
    process.env.REACT_APP_TOUR_UPLOAD_API_BASE_URL ||
    process.env.REACT_APP_VIDEOS_API_BASE_URL;
  if (!url) {
    throw new Error('REACT_APP_TOUR_UPLOAD_API_BASE_URL is not configured');
  }
  return url.replace(/\/$/, '');
}

async function authHeaders(): Promise<Record<string, string>> {
  const idToken = await getAuth().currentUser?.getIdToken();
  if (!idToken) throw new Error('You need to be signed in to upload a tour');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  };
}

async function post<T>(name: string, body: unknown): Promise<T> {
  const res = await fetch(`${baseUrl()}/${name}`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      (json && json.error) || `${name} failed with HTTP ${res.status}`,
    );
  }
  return json as T;
}

// ---------------------------------------------------------------------------
// What a tour folder contains
// ---------------------------------------------------------------------------

/**
 * A file from the picked folder with its path relative to the tour root.
 * `relativePath` uses forward slashes and never starts with the folder's
 * own name — `tourDetails.json`, `floors/floor1/model.drc`, and so on.
 */
export interface TourFile {
  file: File;
  relativePath: string;
}

/**
 * Which files of a built tour the viewer actually reads. Everything else the
 * builder leaves next to them (a 46 MB `model.obj`, a single-file `splat.sog`
 * — the viewer streams `splat/v<N>/` instead — `.DS_Store`) is skipped, which
 * roughly halves the bytes for a mesh-lab tour.
 */
export function isViewerAsset(relativePath: string): boolean {
  const p = relativePath;
  if (p.split('/').some((seg) => seg.startsWith('.'))) return false; // .DS_Store and friends
  if (p === 'tourDetails.json') return true;
  if (/^thumbnail(_\d+)?\.jpe?g$/.test(p)) return true;
  if (/^(flythrough|layer-settings|area-calculation)\.json$/.test(p))
    return true;
  if (
    /^floors\/[^/]+\/(model\.drc|texture\d+k\.jpe?g|material\.mtl|floorPlan\.png)$/.test(
      p,
    )
  )
    return true;
  // Model v2 (Oct 2026): multi-material glTF with draco geometry and KTX2 textures,
  // shipped beside the legacy model.drc + texture ladder.
  if (/^floors\/[^/]+\/model_v2[^/]*\.(gltf|bin|ktx2)$/.test(p)) return true;
  if (
    /^rooms\/[^/]+\/panos\/[^/]+\/(pano\.jpe?g|tiles_config\.json|tiles\/.+)$/.test(
      p,
    )
  )
    return true;
  // 3D flythrough splat (Oct 2026): a streamed SOG named by tourDetails
  // `splat.key` — lod-meta.json plus one folder of webp/json per LOD chunk,
  // in a versioned folder so a retrained splat never mixes with cached chunks.
  if (
    /^splat\/v\d+\/(lod-meta\.json|\d+_\d+\/[A-Za-z0-9_]+\.(json|webp))$/.test(
      p,
    )
  )
    return true;
  return false;
}

/**
 * Normalises a `<input webkitdirectory>` selection into tour files. The
 * browser reports `webkitRelativePath` as `<pickedFolder>/…`; the picked
 * folder's own name is dropped, so it does not matter what the user called
 * it on disk.
 *
 * Throws when the selection does not look like a tour at all.
 */
export function collectTourFiles(fileList: FileList | File[]): {
  files: TourFile[];
  skipped: number;
  totalBytes: number;
} {
  const all = Array.from(fileList);
  const files: TourFile[] = [];
  let skipped = 0;
  let totalBytes = 0;
  for (const file of all) {
    const raw =
      (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
      file.name;
    const parts = raw.split('/').filter(Boolean);
    const relativePath = (parts.length > 1 ? parts.slice(1) : parts).join('/');
    if (!isViewerAsset(relativePath)) {
      skipped += 1;
      continue;
    }
    files.push({ file, relativePath });
    totalBytes += file.size;
  }
  if (!files.some((f) => f.relativePath === 'tourDetails.json')) {
    throw new Error(
      'That folder has no tourDetails.json at its top level — pick the tour folder itself.',
    );
  }
  if (!files.some((f) => /^floors\/[^/]+\/model\.drc$/.test(f.relativePath))) {
    throw new Error(
      'That folder has no floors/<floor>/model.drc — the tour has no 3D model.',
    );
  }
  return { files, skipped, totalBytes };
}

/** Rough summary for the confirmation step. */
export function describeTourFiles(files: TourFile[]): {
  floors: number;
  panos: number;
  tiles: number;
  splatFiles: number;
} {
  const floors = new Set<string>();
  const panos = new Set<string>();
  let tiles = 0;
  let splatFiles = 0;
  for (const f of files) {
    const floor = /^floors\/([^/]+)\//.exec(f.relativePath);
    if (floor) floors.add(floor[1]);
    const pano = /^rooms\/([^/]+)\/panos\/([^/]+)\//.exec(f.relativePath);
    if (pano) panos.add(`${pano[1]}/${pano[2]}`);
    if (/\/tiles\/\d+\//.test(f.relativePath)) tiles += 1;
    if (f.relativePath.startsWith('splat/')) splatFiles += 1;
  }
  return { floors: floors.size, panos: panos.size, tiles, splatFiles };
}

// ---------------------------------------------------------------------------
// The three calls
// ---------------------------------------------------------------------------

export interface BeginResult {
  tourId: string;
  bucket: string;
  prefix: string;
}

export interface FinalizeResult {
  tourId: string;
  path: string;
  floors: number;
  panos: number;
  droppedPanos: Array<{ id: string | null; reason: string }>;
  publicObjects: number;
  totalObjects: number;
  warnings: string[];
  alreadyFinalized?: boolean;
}

export function beginTourUpload(input: {
  name: string;
  address?: string;
  isPrivate?: boolean;
}): Promise<BeginResult> {
  return post<BeginResult>('tourUploadBegin', input);
}

export function finalizeTourUpload(tourId: string): Promise<FinalizeResult> {
  return post<FinalizeResult>('tourUploadFinalize', { tourId });
}

export function abortTourUpload(
  tourId: string,
): Promise<{ tourId: string; deletedObjects: number }> {
  return post('tourUploadAbort', { tourId });
}

// ---------------------------------------------------------------------------
// Streaming the folder up
// ---------------------------------------------------------------------------

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  /** 0..1 fraction complete, by bytes. */
  fraction: number;
  filesDone: number;
  filesTotal: number;
  /** The file most recently started, for the status line. */
  current: string;
}

function contentTypeFor(relativePath: string, file: File): string {
  if (file.type) return file.type;
  if (/\.jpe?g$/i.test(relativePath)) return 'image/jpeg';
  if (/\.png$/i.test(relativePath)) return 'image/png';
  if (/\.webp$/i.test(relativePath)) return 'image/webp';
  if (/\.json$/i.test(relativePath)) return 'application/json';
  if (/\.drc$/i.test(relativePath)) return 'application/octet-stream';
  if (/\.mtl$/i.test(relativePath)) return 'text/plain';
  if (/thumbnail_b64$/.test(relativePath)) return 'text/plain';
  return 'application/octet-stream';
}

function uploadOne(
  prefix: string,
  entry: TourFile,
  onBytes: (delta: number) => void,
  signal: AbortSignal,
): Promise<void> {
  const objectPath = `${prefix}${entry.relativePath}`;
  const objectReference = storageRef(getStorage(), objectPath);
  const task = uploadBytesResumable(objectReference, entry.file, {
    contentType: contentTypeFor(entry.relativePath, entry.file),
  });
  let seen = 0;
  const onAbort = () => task.cancel();
  signal.addEventListener('abort', onAbort, { once: true });
  return new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        onBytes(snap.bytesTransferred - seen);
        seen = snap.bytesTransferred;
      },
      (err) => {
        signal.removeEventListener('abort', onAbort);
        reject(err);
      },
      () => {
        signal.removeEventListener('abort', onAbort);
        onBytes(entry.file.size - seen);
        resolve();
      },
    );
  });
}

/**
 * Streams every file to `prefix` with a few transfers in flight at once. A
 * tile pyramid is hundreds of small files, so the win is concurrency rather
 * than chunking; the per-file resumable upload still covers the 20 MB
 * texture. Each file is retried twice before the whole upload fails.
 */
export async function uploadTourFiles(
  prefix: string,
  files: TourFile[],
  options: {
    concurrency?: number;
    onProgress?: (p: UploadProgress) => void;
    signal?: AbortSignal;
  } = {},
): Promise<void> {
  const concurrency = Math.max(1, options.concurrency ?? 6);
  const signal = options.signal ?? new AbortController().signal;
  const totalBytes = files.reduce((n, f) => n + f.file.size, 0);
  let bytesTransferred = 0;
  let filesDone = 0;
  let current = '';
  const report = () =>
    options.onProgress?.({
      bytesTransferred,
      totalBytes,
      fraction: totalBytes > 0 ? bytesTransferred / totalBytes : 0,
      filesDone,
      filesTotal: files.length,
      current,
    });

  // Big files first so the long transfers overlap the tile flood instead of
  // trailing it.
  const queue = [...files].sort((a, b) => b.file.size - a.file.size);
  let next = 0;
  let failure: Error | null = null;

  // Declared once, outside every loop: the lint rule (no-loop-func) is
  // right that a closure created per iteration over shared counters is easy
  // to get wrong, so the counters are updated through this one function.
  const onBytes = (delta: number) => {
    bytesTransferred += delta;
    report();
  };
  const backoff = (attempt: number) =>
    new Promise<void>((r) => setTimeout(r, 800 * attempt));

  async function worker() {
    while (next < queue.length && !failure && !signal.aborted) {
      const entry = queue[next++];
      current = entry.relativePath;
      report();
      let attempt = 0;
      for (;;) {
        try {
          await uploadOne(prefix, entry, onBytes, signal);
          break;
        } catch (e) {
          if (signal.aborted) return;
          attempt += 1;
          if (attempt > 2) {
            failure = e instanceof Error ? e : new Error(String(e));
            return;
          }
          await backoff(attempt);
        }
      }
      filesDone += 1;
      report();
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, queue.length) }, worker),
  );
  if (signal.aborted) throw new Error('Upload cancelled');
  if (failure) throw failure;
}
